<script setup>
import { ref, onMounted } from "vue";

// const StepItemStyle = defineAsyncComponent(() => import('primevue/stepitemstyle'))
const WrTestimonialCardCarousel = defineAsyncComponent(() =>
  import("~/components/molecules/WrTestimonialCardCarousel.vue")
);
// Animation state
const isLoaded = ref(false);

onMounted(() => {
  setTimeout(() => {
    isLoaded.value = true;
  }, 100);
});

definePageMeta({
  layout: "services-layout",
});

useHead({
  title:
    "Award Winning Software and Web Application Development Agency - India",
  meta: [
    {
      name: "description",
      content:
        "An award winning software, website & app development agency. WebReinvent does not design and build websites, it engineers them and crafts works of art. Highly reputed global client base yet affordable prices. Need we say more about us?",
    },
    {
      name: "keywords",
      content:
        "WebReinvent company, WebReinvent about us, web development company, software development company, app development company, digital transformation company, webreinvent team, IT services company, technology partner, webreinvent profile",
    },
  ],
});

// Company navigation items
const companyItems = ref([
  {
    title: "About Us",
    description:
      "Learn about our mission, vision, and the story behind our company",
    icon: "🏢",
    link: "/about-us",
    color: "from-purple-500 to-indigo-500",
    bgColor: "bg-purple-50 dark:bg-purple-900/20",
    borderColor: "border-purple-200 dark:border-purple-800",
  },
  {
    title: "Case Studies",
    description: "Explore our successful projects and client success stories",
    icon: "📊",
    link: "/case-studies",
    color: "from-purple-500 to-indigo-500",
    bgColor: "bg-purple-50 dark:bg-purple-900/20",
    borderColor: "border-purple-200 dark:border-purple-800",
  },
  {
    title: "How We Work",
    description: "Discover our proven methodology and development process",
    icon: "⚙️",
    link: "/how-we-work",
    color: "from-purple-500 to-indigo-500",
    bgColor: "bg-purple-50 dark:bg-purple-900/20",
    borderColor: "border-purple-200 dark:border-purple-800",
  },
  {
    title: "Capabilities",
    description: "Explore our technical expertise and service offerings",
    icon: "🚀",
    link: "/capabilities",
    color: "from-purple-500 to-indigo-500",
    bgColor: "bg-purple-50 dark:bg-purple-900/20",
    borderColor: "border-purple-200 dark:border-purple-800",
  },
  {
    title: "Dedicated Team",
    description: "Meet our talented professionals and their expertise",
    icon: "👥",
    link: "/dedicated-team",
    color: "from-purple-500 to-indigo-500",
    bgColor: "bg-purple-50 dark:bg-purple-900/20",
    borderColor: "border-purple-200 dark:border-purple-800",
  },
  {
    title: "Staff Augmentation",
    description: "Scale your team with our skilled developers and specialists",
    icon: "🔧",
    link: "/staff-augmentation",
    color: "from-purple-500 to-indigo-500",
    bgColor: "bg-purple-50 dark:bg-purple-900/20",
    borderColor: "border-purple-200 dark:border-purple-800",
  },
  {
    title: "Testimonials",
    description: "Read what our clients say about working with us",
    icon: "⭐",
    link: "/testimonials",
    color: "from-purple-500 to-indigo-500",
    bgColor: "bg-purple-50 dark:bg-purple-900/20",
    borderColor: "border-purple-200 dark:border-purple-800",
  },
  {
    title: "Careers",
    description:
      "Join our team and build your career with exciting opportunities",
    icon: "💼",
    link: "/career",
    color: "from-purple-500 to-indigo-500",
    bgColor: "bg-purple-50 dark:bg-purple-900/20",
    borderColor: "border-purple-200 dark:border-purple-800",
  },
]);

// Stats data
const stats = ref([
  { number: "800+", label: "Projects Completed" },
  { number: "250+", label: "Happy Clients" },
  { number: "100+", label: "Team Members" },
  { number: "15+", label: "Years Experience" },
]);
</script>

<template>
  <!-- Hero Section -->
  <section
    class="relative bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 overflow-hidden"
  >
    <!-- Background Pattern -->
    <div class="absolute inset-0 opacity-10">
      <div
        class="absolute inset-0 bg-[radial-gradient(circle_at_2px_2px,rgba(255,255,255,0.3)_1px,transparent_0)] bg-[length:40px_40px]"
      ></div>
    </div>

    <!-- Floating Elements -->
    <div class="absolute inset-0 overflow-hidden">
      <div
        class="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-xl animate-pulse"
      ></div>
      <div
        class="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-full blur-xl animate-pulse delay-1000"
      ></div>
      <div
        class="absolute bottom-32 left-1/4 w-40 h-40 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full blur-xl animate-pulse delay-500"
      ></div>
    </div>

    <div class="relative z-10 max-w-7xl mx-auto px-4 py-20">
      <div class="text-center">
        <!-- Badge -->
        <div
          :class="[
            'inline-flex mt-16 items-center px-6 py-1.5 rounded-full border border-white/20 backdrop-blur-sm mb-8 transition-all duration-1000',
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
          ]"
        >
          <div class="flex items-center gap-3">
            <div class="relative">
              <div
                class="size-2 bg-green-400 rounded-full animate-ping absolute"
              ></div>
              <div class="size-2 bg-green-400 rounded-full"></div>
            </div>
            <span class="text-white/90 font-medium text-sm"
              >Trusted by Industry Leaders</span
            >
          </div>
        </div>

        <!-- Main Heading -->
        <div
          :class="[
            'space-y-6 mb-12 transition-all duration-1000 delay-200',
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8',
          ]"
        >
          <h1 class="text-5xl lg:text-7xl font-bold leading-tight">
            <span class="block text-white mb-2">Discover Our</span>
            <WrGradientText
              append-class="py-0 bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-3.57%,_var(--text-gradient-2)_50.72%)]"
            >
              Company
            </WrGradientText>
          </h1>
          <p class="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
            Explore everything about our company - from our story and
            capabilities to our team and career opportunities.
            <span class="text-purple-400 font-semibold"
              >Your journey with us starts here.</span
            >
          </p>
        </div>

        <!-- Stats -->
        <div
          :class="[
            'grid grid-cols-2 lg:grid-cols-4 gap-8 mb-16 transition-all duration-1000 delay-400',
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8',
          ]"
        >
          <div
            v-for="(stat, index) in stats"
            :key="stat.label"
            class="text-center"
            :style="{ transitionDelay: `${600 + index * 100}ms` }"
          >
            <div class="text-3xl lg:text-4xl font-bold text-white mb-2">
              {{ stat.number }}
            </div>
            <div class="text-gray-400 text-sm lg:text-base">
              {{ stat.label }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Company Navigation Cards -->
  <section class="relative -mt-16 z-20 max-w-7xl mx-auto px-4 pb-20">
    <div
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
    >
      <div
        v-for="(item, index) in companyItems"
        :key="item.title"
        :class="[
          'group relative transition-all duration-500 backdrop-blur-sm',
          isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8',
        ]"
        :style="{ transitionDelay: `${800 + index * 100}ms` }"
      >
        <!-- Card -->

        <div
          :class="[
            'relative h-full p-6 rounded-2xl border-2 transition-all duration-500 hover:scale-[1.01] hover:shadow-md cursor-pointer overflow-hidden',
            item.bgColor,
            item.borderColor,
            'hover:border-opacity-50',
          ]"
        >
          <!-- Gradient Overlay -->
          <div
            :class="[
              'absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 bg-gradient-to-br',
              item.color,
            ]"
          ></div>

          <!-- Content -->
          <div class="relative z-10 h-full flex flex-col">
            <!-- Icon -->
            <div class="mb-4">
              <div
                :class="[
                  'w-16 h-16 rounded-2xl flex items-center justify-center text-2xl transition-all duration-300 group-hover:scale-[1.01] bg-gradient-to-br',
                  item.color,
                ]"
              >
                <span class="filter drop-shadow-sm">{{ item.icon }}</span>
              </div>
            </div>

            <!-- Title -->
            <h2
              class="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-[var(--primary)] dark:group-hover:text-purple-400 transition-colors duration-300"
            >
              {{ item.title }}
            </h2>

            <!-- Description -->
            <p
              class="text-gray-600 dark:text-gray-300 text-sm leading-relaxed flex-1 mb-4"
            >
              {{ item.description }}
            </p>

            <!-- Arrow -->
            <div class="flex items-center justify-between">
              <div class="flex-1"></div>
              <div
                class="w-8 h-8 rounded-full bg-white dark:bg-gray-800 shadow-lg flex items-center justify-center group-hover:shadow-xl transition-all duration-300 group-hover:scale-110"
              >
                <svg
                  class="w-4 h-4 text-gray-600 dark:text-gray-300 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-all duration-300 group-hover:translate-x-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </div>
            </div>
          </div>
          <NuxtLink
            v-if="!item.link.includes('https')"
            :to="item.link"
            class="absolute inset-0 w-full h-full z-10"
            :aria-label="item.link"
          >
          </NuxtLink>
          <NuxtLink
            v-else
            :to="item.link"
            :aria-label="item.link"
            target="_blank"
            rel="noopener noreferrer"
            class="absolute inset-0 w-full h-full z-10"
          />
        </div>
      </div>
    </div>
  </section>

  <WrBannerCTA
    heading="Ready to Work With Us?"
    text="Let’s discuss your project and see how we can help you achieve your goals with our expertise and dedication."
    :link="{ link: '/contact-us', text: 'Let’s Get Cracking!' }"
  />

  <!-- Enhanced Testimonials Section -->
  <section class="relative z-10 mt-20">
    <div class="max-w-screen-xl mx-auto px-4">
      <div class="text-center mb-16">
        <h2 class="text-4xl lg:text-5xl font-bold mb-11">
          <WrGradientText
            text="Trusted for excellence and efficiency"
            append-class="bg-gradient-to-r from-gray-900 via-purple-800 to-blue-800 dark:from-white dark:via-purple-200 dark:to-blue-200 bg-clip-text text-transparent"
          />
        </h2>
        <p class="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          WebReinvent is trusted by leading organisations to enhance their
          development and operational efficiency.
        </p>
      </div>

      <WrTestimonialCardCarousel class="mb-12" />

      <div class="text-center">
        <WrButton link="/testimonials" container-class="mx-auto">
          View All Testimonials
        </WrButton>
      </div>
    </div>
  </section>
</template>

<style scoped>
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.grid > div {
  animation: fadeInUp 0.6s ease-out forwards;
}
</style>
